/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css } from '@compiled/react';

import { jsx } from '@atlaskit/css';

import { type ComboBoxField, type PageAnnotations, type TextField } from './types';

const formInputBaseStyles = css({
	// eslint-disable-next-line  @atlaskit/design-system/use-tokens-typography
	fontFamily: 'Arial', // Ensures consistent font across multi-line and single line inputs
	// eslint-disable-next-line  @atlaskit/design-system/use-tokens-typography
	lineHeight: 1.25, // Ensures consistent line height across multi-line and single line inputs
	appearance: 'none',
	border: 'none',
	resize: 'none',
	padding: 0,
});

/**
 * By default, the visual pdf form elements are rendered by pdfium server side. Therefore we only
 * show visibility of the html input if the user interacts with it. This allows us to show visually
 * accurate PDF form elements while still allowing the user to interact with the input.
 */
const invisibleInputStyles = css({
	backgroundColor: 'transparent',
	color: 'transparent',
	overflow: 'hidden',
	'&:focus': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: 'white',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: 'black',
		overflow: 'auto',
	},
});

const textInputStyles = css({
	position: 'absolute',
	height: '100%',
	boxSizing: 'border-box',
	width: '100%',
});

const foreignObjectProps = {
	xmlns: 'http://www.w3.org/1999/xhtml',
};

type TextInputProps = {
	style: React.CSSProperties;
	value: string;
	as: 'input' | 'textarea';
};

const TextInput = ({ as: Component, value, style }: TextInputProps) => (
	<Component
		{...foreignObjectProps}
		css={[formInputBaseStyles, textInputStyles, invisibleInputStyles]}
		style={style}
		onKeyUp={(e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			// stop propagation of the arrow key events because they can be used to navigate viewports
			if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
				e.stopPropagation();
			}
		}}
		value={value}
		type="text"
		readOnly
	/>
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const TextInputFormField = ({
	field,
	dataTestId,
}: {
	field: TextField;
	dataTestId?: string;
}): JSX.Element => {
	const style: React.CSSProperties = { ['fontSize']: `${field.f}px` };

	return (
		<foreignObject
			x={field.x}
			y={-field.y}
			width={field.w}
			height={field.h}
			data-testid={dataTestId}
		>
			<TextInput style={style} as={field.multiline ? 'textarea' : 'input'} value={field.text} />
		</foreignObject>
	);
};

const comboBoxStyles = css({
	display: 'grid',
	gridTemplateColumns: '1fr auto',
	alignItems: 'center',
	justifyContent: 'space-between',
	position: 'absolute',
	height: '100%',
	width: '100%',
});

const comboBoxInputStyles = css({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const ComboBoxFormField = ({
	field,
	dataTestId,
}: {
	field: ComboBoxField;
	dataTestId?: string;
}): JSX.Element => {
	return (
		<foreignObject
			x={field.x}
			y={-field.y}
			width={field.w}
			height={field.h}
			data-testid={dataTestId}
		>
			<div {...foreignObjectProps} css={comboBoxStyles}>
				<input
					css={[formInputBaseStyles, comboBoxInputStyles, invisibleInputStyles]}
					style={{ ['fontSize']: `${field.f}px` }}
					type="text"
					value={field.text}
					// stop propagation of the arrow key events because they can be used to navigate viewports
					onKeyUp={(e) => {
						if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
							e.stopPropagation();
						}
					}}
					readOnly
				/>
			</div>
		</foreignObject>
	);
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const Annotations = ({ annotations }: { annotations: PageAnnotations }): JSX.Element => {
	return (
		<React.Fragment>
			{annotations.text_form_fields.map((field, i) => {
				return <TextInputFormField field={field} dataTestId={`text-form-field-${i}`} key={i} />;
			})}
			{annotations.combobox_form_fields.map((field, i) => {
				return <ComboBoxFormField field={field} dataTestId={`combobox-form-field-${i}`} key={i} />;
			})}
		</React.Fragment>
	);
};
